// redux/automations/thunks.ts

import { createAsyncThunk } from "@reduxjs/toolkit";
import * as automationApi from "../services/automationApi";
import { getAllCompanies } from "../services/companyService";
import { getErrorMessage } from "../utils/errorUtils";

// =========================
// COMPANY THUNKS
// =========================

export const fetchCompanies = createAsyncThunk(
  "automations/fetchCompanies",
  async (_, { rejectWithValue }) => {
    try {
      const signature = localStorage.getItem("signature");
      return await getAllCompanies(signature);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// =========================
// NODE CONFIGURATION THUNKS
// =========================

export const fetchAllNodeConfigs = createAsyncThunk(
  "automations/fetchAllNodeConfigs",
  async (
    {
      companyId,
      companyType,
      workflowType,
    }: { companyId: string; companyType: string; workflowType: string },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.getAllNodeConfigs(
        signature,
        companyId,
        companyType,
        workflowType
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchNodeDynamicData = createAsyncThunk(
  "automations/fetchNodeDynamicData",
  async (
    {
      companyId,
      companyType,
      nodeKey,
    }: { companyId: string; companyType: string; nodeKey: string },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      const response = await automationApi.getNodeDynamicData(
        signature,
        companyId,
        companyType,
        nodeKey
      );
      return { nodeKey, data: response };
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const validateNode = createAsyncThunk(
  "automations/validateNode",
  async (
    {
      companyId,
      companyType,
      nodeKey,
      config,
    }: { companyId: string; companyType: string; nodeKey: string; config: any },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.validateNodeConfig(
        signature,
        companyId,
        companyType,
        nodeKey,
        config
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// =========================
// WORKFLOW MANAGEMENT THUNKS
// =========================

export const saveWorkflow = createAsyncThunk(
  "automations/saveWorkflow",
  async (
    {
      companyId,
      companyType,
      workflow,
      userId,
    }: {
      companyId: string;
      companyType: string;
      workflow: any;
      userId?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.saveWorkflow(
        signature,
        companyId,
        companyType,
        workflow,
        userId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchAllWorkflows = createAsyncThunk(
  "automations/fetchAllWorkflows",
  async (
    {
      companyId,
      companyType,
      params,
    }: {
      companyId: string;
      companyType: string;
      params?: {
        page?: number;
        limit?: number;
        search?: string;
        workflow_type?: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.getAllWorkflows(
        signature,
        companyId,
        companyType,
        params
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchWorkflow = createAsyncThunk(
  "automations/fetchWorkflow",
  async (
    {
      companyId,
      companyType,
      workflowId,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.getWorkflow(
        signature,
        companyId,
        companyType,
        workflowId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const deleteWorkflow = createAsyncThunk(
  "automations/deleteWorkflow",
  async (
    {
      companyId,
      companyType,
      workflowId,
      userId,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
      userId?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      await automationApi.deleteWorkflow(
        signature,
        companyId,
        companyType,
        workflowId,
        userId
      );
      return workflowId;
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const toggleWorkflowStatus = createAsyncThunk(
  "automations/toggleWorkflowStatus",
  async (
    {
      companyId,
      companyType,
      workflowId,
      isActive,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
      isActive: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      await automationApi.toggleWorkflowStatus(
        signature,
        companyId,
        companyType,
        workflowId,
        isActive
      );
      return { workflowId, isActive };
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateWorkflowSettings = createAsyncThunk(
  "automations/updateWorkflowSettings",
  async (
    {
      companyId,
      companyType,
      workflowId,
      settings,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
      settings: any;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.updateWorkflowSettings(
        signature,
        companyId,
        companyType,
        workflowId,
        settings
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchWorkflowById = createAsyncThunk(
  "automations/fetchWorkflowById",
  async (
    {
      companyId,
      companyType,
      workflowId,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.getWorkflowById(
        signature,
        companyId,
        companyType,
        workflowId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const runWorkflowManually = createAsyncThunk(
  "automations/runWorkflowManually",
  async (
    {
      companyId,
      companyType,
      workflowId,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      await automationApi.runWorkflowManually(
        signature,
        companyId,
        companyType,
        workflowId
      );
      return { workflowId };
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// =========================
// WORKFLOW EXECUTION THUNKS
// =========================

export const fetchExecutionHistory = createAsyncThunk(
  "automations/fetchExecutionHistory",
  async (
    {
      companyId,
      companyType,
      workflowId,
      page = 1,
      limit = 10,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
      page?: number;
      limit?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.getExecutionHistory(
        signature,
        companyId,
        companyType,
        workflowId,
        page,
        limit
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const fetchExecutionDetails = createAsyncThunk(
  "automations/fetchExecutionDetails",
  async (
    {
      companyId,
      companyType,
      executionId,
    }: {
      companyId: string;
      companyType: string;
      executionId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.getExecutionDetails(
        signature,
        companyId,
        companyType,
        executionId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// =========================
// WORKFLOW VERSION THUNKS
// =========================

export const fetchVersionHistory = createAsyncThunk(
  "automations/fetchVersionHistory",
  async (
    {
      companyId,
      companyType,
      workflowId,
    }: {
      companyId: string;
      companyType: string;
      workflowId: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.getVersionHistory(
        signature,
        companyId,
        companyType,
        workflowId
      );
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// =========================
// WORKFLOW VALIDATION THUNKS
// =========================

export const validateWorkflow = createAsyncThunk(
  "automations/validateWorkflow",
  async ({ workflow }: { workflow: any }, { rejectWithValue }) => {
    try {
      const signature = localStorage.getItem("signature");
      return await automationApi.validateWorkflow(signature, workflow);
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
